import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "./AuthContext";

const API_BASE_URL = "https://chemplastemployeetrainingtest.azurewebsites.net";

const EmployeeList = () => {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState("employee");
  const [sortKey, setSortKey] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");

  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/employees`)
      .then((response) => {
        setEmployees(response.data);
        setFilteredEmployees(response.data);
      })
      .catch((error) => console.error("Error fetching employees:", error));
  }, []);

  useEffect(() => {
    if (searchTerm.length > 0) {
      const delayDebounceFn = setTimeout(() => {
        const url = searchType === "employee"
          ? `${API_BASE_URL}/employees/${searchTerm}`
          : `${API_BASE_URL}/parts/${searchTerm}`;
        axios.get(url)
          .then((response) => setFilteredEmployees(response.data))
          .catch((error) => console.error("Error fetching search results:", error));
      }, 300);
      return () => clearTimeout(delayDebounceFn);
    } else {
      setFilteredEmployees(employees);
    }
  }, [searchTerm, searchType, employees]);

  // Sorting logic
  const handleSort = (key) => {
    const order = sortKey === key && sortOrder === "asc" ? "desc" : "asc";
    setSortKey(key);
    setSortOrder(order);
  };

  const sortedEmployees = [...filteredEmployees].sort((a, b) => {
    let valA = a[sortKey];
    let valB = b[sortKey];

    // Convert date string to timestamp for proper comparison
    if (sortKey === "last_updated") {
      valA = new Date(valA).getTime();
      valB = new Date(valB).getTime();
    }

    if (valA < valB) return sortOrder === "asc" ? -1 : 1;
    if (valA > valB) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  const getProficiencyColor = (proficiency) => {
    if (proficiency >= 4) return "green";
    if (proficiency >= 3) return "yellow";
    if (proficiency >= 2) return "orange";
    if (proficiency >= 1) return "red";
    return "gray";
  };

  const handleAddTraining = () => {
    if (user) navigate("/edit");
    else navigate("/login");
  };

  return (
    <div className="container mt-4">
      <h2 className="mb-3">Employee Training Lookup</h2>

      <div className="mb-3 text-end">
        <button className="btn btn-primary" onClick={handleAddTraining}>
          ➕ Add Training Info
        </button>
      </div>

      <div className="d-flex">
        <input
          type="text"
          className="form-control mb-2"
          placeholder={`Search by ${searchType === "employee" ? "Employee Name" : "Part Number"}`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select className="form-select mx-2" onChange={(e) => setSearchType(e.target.value)}>
          <option value="employee">Search by Employee</option>
          <option value="part">Search by Part</option>
        </select>
      </div>

      {/* Color Key */}
      <div className="mb-3">
        <h5>Proficiency Level Key:</h5>
        <div className="d-flex align-items-center">
          <div style={{ width: "20px", height: "20px", backgroundColor: "red", marginRight: "10px" }}></div>
          <span>1.0 - 1.5 (Low Proficiency)</span>
        </div>
        <div className="d-flex align-items-center">
          <div style={{ width: "20px", height: "20px", backgroundColor: "orange", marginRight: "10px" }}></div>
          <span>2.0 - 2.5 (Medium-Low Proficiency)</span>
        </div>
        <div className="d-flex align-items-center">
          <div style={{ width: "20px", height: "20px", backgroundColor: "yellow", marginRight: "10px" }}></div>
          <span>3.0 - 3.5 (Medium-High Proficiency)</span>
        </div>
        <div className="d-flex align-items-center">
          <div style={{ width: "20px", height: "20px", backgroundColor: "green", marginRight: "10px" }}></div>
          <span>4.0 (High Proficiency)</span>
        </div>
      </div>

      {/* Employee Table */}
      <table className="table table-bordered">
        <thead className="thead-dark">
          <tr>
            <th onClick={() => handleSort("name")} style={{ cursor: "pointer" }}>
              Employee Name {sortKey === "name" && (sortOrder === "asc" ? "▲" : "▼")}
            </th>
            <th onClick={() => handleSort("part_number")} style={{ cursor: "pointer" }}>
              Part Number {sortKey === "part_number" && (sortOrder === "asc" ? "▲" : "▼")}
            </th>
            <th onClick={() => handleSort("proficiency")} style={{ cursor: "pointer" }}>
              Proficiency {sortKey === "proficiency" && (sortOrder === "asc" ? "▲" : "▼")}
            </th>
            <th onClick={() => handleSort("last_updated")} style={{ cursor: "pointer" }}>
              Last Updated {sortKey === "last_updated" && (sortOrder === "asc" ? "▲" : "▼")}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedEmployees.length > 0 ? (
            sortedEmployees.map((employee, index) => (
              <tr key={index}>
                <td>{employee.name || "N/A"}</td>
                <td>{employee.part_number || "N/A"}</td>
                <td
                  style={{
                    backgroundColor: getProficiencyColor(employee.proficiency),
                    color: "black",
                    fontWeight: "bold"
                  }}
                >
                  {employee.proficiency || "N/A"}
                </td>
                <td>
                  {employee.last_updated
                    ? new Date(employee.last_updated).toLocaleDateString("en-us", {
                                                                        year: "numeric",
                                                                        month: "long",
                                                                        day: "numeric",
                                                                       timeZone: "UTC"
                                                                    })
                    : "N/A"}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="4" className="text-center">No Results Found</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default EmployeeList;
